// Web Push plumbing shared by Settings and app start-up.

import { api } from './api.ts';
import { devicePrefs } from './device.ts';

export const pushSupported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64url.length % 4)) % 4);
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function sameKey(sub: PushSubscription, key: Uint8Array): boolean {
  const k = sub.options?.applicationServerKey;
  if (!k) return true; // can't tell — assume fine
  const a = new Uint8Array(k);
  return a.length === key.length && a.every((v, i) => v === key[i]);
}

/** A subscription for the server's current key (resubscribes if the key changed). */
export async function ensureSubscription(publicKey: string): Promise<PushSubscription> {
  const reg = await navigator.serviceWorker.ready;
  const key = keyBytes(publicKey);
  const existing = await reg.pushManager.getSubscription();
  if (existing && sameKey(existing, key)) return existing;
  await existing?.unsubscribe().catch(() => undefined);
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
}

/** Keep the server's copy fresh — push services rotate endpoints, and the server's key can change. */
export async function refreshPushSubscription(): Promise<void> {
  if (!pushSupported || Notification.permission !== 'granted') return;
  const d = devicePrefs();
  if (!d.key || !(await currentSubscription().catch(() => null))) return;
  try {
    const { publicKey } = await api<{ publicKey: string }>(`/api/push?device=${encodeURIComponent(d.deviceId)}`);
    // Turning alerts off removes the browser's subscription too, so one that
    // still exists means "wanted" — re-register even if the server dropped it.
    const sub = await ensureSubscription(publicKey);
    await api('/api/push/subscribe', { body: { subscription: sub.toJSON(), device: d.deviceId, person: d.personId ?? null } });
  } catch {
    /* offline — try next launch */
  }
}
